import { useCallback, useEffect, useRef, useState } from 'react';
import { UploadCloud, FolderOpen, AlertCircle } from 'lucide-react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import UploadItem from './UploadItem';
import { useUploads } from '../../context/UploadContext';
import { ACCEPT_ATTRIBUTE } from '../../utils/fileTypes';
import cn from '../../utils/cn';

export default function UploadDialog({ open, onClose, folderId, folderName = 'Home' }) {
  const { items, addFiles, retry, cancel, remove, maxSizeMb, suppressTray, closeTray, activeCount } = useUploads();
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState([]);
  const inputRef = useRef(null);

  const queue = items.filter((i) => i.folderId === folderId);

  // While this dialog is open it *is* the progress view; the global one would stack a
  // second modal on top of it showing the same rows.
  useEffect(() => {
    suppressTray(open);
    return () => suppressTray(false);
  }, [open, suppressTray]);

  const close = useCallback(() => {
    // Nothing still running means there is nothing left to watch, so dismissing this should
    // not hand the user a second dialog to dismiss. With uploads in flight the global view
    // takes over, which is the point of it surviving navigation.
    if (activeCount === 0) closeTray();
    onClose?.();
  }, [activeCount, closeTray, onClose]);

  const handleFiles = (fileList) => {
    const result = addFiles(fileList, folderId);
    setRejected(result.rejected);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      // Wide enough for one pipeline stage per line, matching the global progress view.
      width={720}
      title="Upload documents"
      subtitle={`Destination: ${folderName}`}
      icon={
        <div className="stat-card__icon" aria-hidden="true">
          <UploadCloud size={16} />
        </div>
      }
      footer={
        <>
          <span className="meta" style={{ marginRight: 'auto' }}>
            {queue.length > 0
              ? `${queue.filter((i) => i.status === 'done').length} of ${queue.length} complete`
              : `Maximum ${maxSizeMb} MB per file`}
          </span>
          <Button variant={queue.length ? 'primary' : 'ghost'} onClick={close}>
            {queue.length ? 'Done' : 'Cancel'}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
        <div
          className={cn('dropzone', over && 'is-over')}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        >
          <div className="dropzone__icon">
            <UploadCloud size={22} />
          </div>
          <div className="stack" style={{ gap: 3, alignItems: 'center' }}>
            <strong style={{ fontSize: 'var(--fs-body)' }}>Drag &amp; drop files here</strong>
            <span className="meta">or</span>
          </div>
          <Button variant="secondary" icon={FolderOpen} onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
            Browse files
          </Button>
          <span className="meta" style={{ maxWidth: 340 }}>
            PDF, Word, Excel, PowerPoint, images, text and archives. Up to {maxSizeMb} MB each.
          </span>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTRIBUTE}
            className="sr-only"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {rejected.length > 0 && (
          <div className="banner banner--warning">
            <AlertCircle size={16} />
            <div className="stack" style={{ gap: 3 }}>
              <strong>{rejected.length} file(s) were not added</strong>
              {rejected.slice(0, 3).map((r) => (
                <span key={r.name} className="meta">
                  {r.name} — {r.reason}
                </span>
              ))}
            </div>
          </div>
        )}

        {queue.length > 0 && (
          <div className="stack">
            <div className="overline" style={{ marginBottom: 'var(--sp-1)' }}>
              Uploading
            </div>
            {queue.map((item) => (
              <UploadItem key={item.id} item={item} onRetry={retry} onCancel={cancel} onRemove={remove} />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
